import React from "react";
import { AnimatePresence, motion } from "motion/react";
import { X, Undo2, BadgeCheck } from "lucide-react";

const formatTime = (ts) => {
  if (!ts) return "--:--";
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const fmt = (s) => {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

export default function AllDoneModal({
  open,
  onClose,
  onUndo,
  checkInAt,
  checkOutAt,
  breakCount,
  breakSec,
  workSec,
}) {
  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center px-4"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: "spring", stiffness: 240, damping: 20 }}
          >
            <div
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-emerald-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>

              <div className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <BadgeCheck className="w-6 h-6 text-slate-800" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800">All Done!</h2>
                </div>

                {/* animated badge like figma feel */}
                <motion.div
                  className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 relative overflow-hidden"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                    <span className="inline-flex items-center gap-2">
                      ✅ Day Complete!
                    </span>
                  </div>

                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    <div className="flex justify-between">
                      <span>Check-in:</span>
                      <span className="font-semibold">{formatTime(checkInAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Check-out:</span>
                      <span className="font-semibold">{formatTime(checkOutAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Breaks taken:</span>
                      <span className="font-semibold">{breakCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total break time:</span>
                      <span className="font-semibold text-orange-600">{fmt(breakSec)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-emerald-200">
                      <span>Total work time:</span>
                      <span className="font-semibold text-emerald-700">{fmt(workSec)}</span>
                    </div>
                  </div>

                  {/* soft glow */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-300/15 to-transparent pointer-events-none" />
                </motion.div>

                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  ⚠️ Checked out by mistake? You can undo below.
                </div>

                <button
                  onClick={onUndo}
                  className="mt-5 w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20 transition"
                >
                  <Undo2 className="w-5 h-5" />
                  Undo Check-out
                </button>

                <button
                  onClick={onClose}
                  className="mt-3 w-full h-11 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold transition"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}